import React, {Fragment, useEffect, useState} from 'react';
import {Accept, useDropzone} from "react-dropzone";
import {Box, Button, LinearProgress, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {isBrowser} from 'react-device-detect';
import Resizer from "react-image-file-resizer";
import {getStandardResolutionByName} from "../state/commonFunctions";

export interface FileUploaderProps {
    onUpload: Function;
    onRemove?: Function;
    uploadedFiles?: File[];
    acceptedFiles?: Accept;
    label?: string;
    errorMessage?: string;
    isDirectUpload?: boolean;
    progress?: number;
    isError?: boolean;
    disabled?: boolean;
}

/**
 * This component is used to display a file uploader
 * @param onUpload You have to pass your function to use the value of component
 * Ex. if you use Formik your onUpload function have been this : onUpload={(file:File) => {setFieldValue('fieldName', file)}}
 * @param onRemove You have to pass your function to remove the value of component
 * Ex. if you use Formik your onRemove function have been this : onRemove={() => {setFieldValue('fieldName', null)}}
 * @param acceptedFiles You can find some Ex. here https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types or leave it empty to accept all files
 * @param label
 * @param errorMessage
 * A full example of import can be this:
 * <FileUploader onUpload={(file:File) => {meta.setFieldValue('fieldName', file)}} onRemove={() => {meta.setFieldValue('fieldName', null)}} acceptedFiles={{'image/png': ['.png'],'text/html': ['.html', '.htm'], 'text/csv': ['.csv'], 'text/xls': ['.xls', '.xlsx']}}/>
 * @param props
 * @constructor
 */

const FileUploader = (props: FileUploaderProps) => {
    const theme = useTheme();
    const {t} = useTranslation('translation');

    const [over, setOver] = useState(false);
    const [progress, setProgress] = useState(props.uploadedFiles && props.uploadedFiles.length > 0 ? 100 : 0);
    const [dropped, setDropped] = useState(props.uploadedFiles && props.uploadedFiles.length > 0);
    const [uploadedFiles, setUploadedFiles] = useState<any>(props.uploadedFiles ? props.uploadedFiles : []);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(props.errorMessage ? props.errorMessage : t('error.fileNotUploaded'));

    // @ts-ignore
    const maxResolution = getStandardResolutionByName(window.appConfig.MAX_RESOLUTION_FOR_IMG_RESIZE);
    // @ts-ignore
    const minResolution = getStandardResolutionByName(window.appConfig.MIN_RESOLUTION_FOR_IMG_RESIZE);
    // @ts-ignore
    const quality = window.appConfig.QUALITY_FOR_IMG_RESIZE || 100;

    const classes = {
        fu_container: {
            color: theme.palette.primary.main,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            border: '4px dashed #E2E2E2',
            paddingTop: '16px',
            paddingBottom: '16px',
            height: '136px',
            justifyContent: 'center'
        },
        fu_containerOver: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(1, 30, 65, 0.1)',
            border: '4px dashed #011E41',
            paddingTop: '16px',
            paddingBottom: '16px',
            height: '136px',
            justifyContent: 'center'
        },
        fu_content: {
            textAlign: 'center',
        },
        root: {
            height: 4
        },
        bar: {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} ${100 - progress}%, ${theme.palette.accent.main} 100%)`,
        },
        barError: {
            borderRadius: 4,
            background: `${theme.palette.error.main}`,
        },
        button: {
            marginTop: 2,
            marginBottom: 2,
            borderRadius: 50,
            backgroundColor: 'rgba(1, 30, 65, 0.1)',
            color: theme.palette.primary.main,
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            '&:hover': {
                backgroundColor: 'rgba(1, 30, 65, 0.3)',
            }
        }

    };

    const onDragEnter = () => {
        setOver(true);
    }

    const onDragLeave = () => {
        setOver(false);
    }

    const onDrop = (files: any) => {
        setOver(false);
        setDropped(true);
        setUploadedFiles(files);
        files.map(async (file: File) => {
            try {
                let resizedFile = file?.type.startsWith('image') ? await resizeFile(file) : file;
                props.onUpload(resizedFile);
                if (!props.isDirectUpload) {
                    const timer = setInterval(() => {
                        setProgress((prevProgress) => {
                            if (prevProgress === 100) {
                                clearInterval(timer);
                            }
                            return prevProgress >= 100 ? 100 : prevProgress + 10;
                        });
                    }, 200);
                }
            } catch (e) {
                setProgress(100);
                setError(true);
            }
        })
    }

    const resizeFile = (file: File) =>
        new Promise((resolve) => {
            Resizer.imageFileResizer(
                file,
                maxResolution?.width,
                maxResolution?.height,
                "JPEG",
                quality,
                0,
                (uri: any) => {
                    resolve(uri);
                },
                "file",
                minResolution?.width,
                minResolution?.height
            );
        });

    const onDropRejected = (e: any) => {
        setProgress(100);
        switch (e[0].errors[0].code) {
            case 'file-invalid-type':
                setErrorMessage(t('error.fileTypeNotAccepted'));
                break;
            case 'file-too-large':
                setErrorMessage(t('error.fileTooLarge'));
                break;
            default:
                setErrorMessage(t('error.fileNotUploaded'));
        }
        setError(true);
    }

    const cancel = () => {
        setOver(false);
        setDropped(false);
        setError(false);
        setErrorMessage(props.errorMessage ? props.errorMessage : t('error.fileNotUploaded'));
        setUploadedFiles([]);
        setProgress(0);
        !!props.onRemove && props.onRemove();
    }

    const {getRootProps, getInputProps, open} = useDropzone({
        maxFiles: 1,
        accept: props.acceptedFiles ? props.acceptedFiles : undefined,
        multiple: false,
        noClick: true,
        noKeyboard: true,
        onDragEnter: onDragEnter,
        onDragLeave: onDragLeave,
        onDrop: files => onDrop(files),
        onDropRejected: onDropRejected,
        disabled: props.disabled
    });

    const acceptedFilesLabel = props.acceptedFiles ? Object.entries(props.acceptedFiles).map(([key, value]) => {
        return value.join(', ');
    }).join(', ') : '';

    useEffect(() => {
        if (props.isDirectUpload) {
            if (props.isError) {
                setProgress(100);
                setError(true);
            } else if (props.progress !== undefined) {
                setProgress(props.progress);
            }
        }
    }, [props.isDirectUpload, props.progress, props.isError]);

    useEffect(() => {
        if (props.uploadedFiles && props.uploadedFiles.length > 0) {
            setDropped(true);
            setProgress(100);
            setUploadedFiles(props.uploadedFiles);
        } else {
            setOver(false);
            setDropped(false);
            setError(false);
            setErrorMessage(props.errorMessage ? props.errorMessage : t('error.fileNotUploaded'));
            setUploadedFiles([]);
            setProgress(0);
        }
    }, [props.uploadedFiles]);

    return (
        <Fragment>
            {props.label && <Typography variant="body1" color="primary" mb={1}>{props.label}</Typography>}
            <Box sx={over ? classes.fu_containerOver : classes.fu_container} {...getRootProps({ className: 'dropzone' })}>
                {!dropped || (uploadedFiles.length == 0 && !error) ?
                    <Fragment>
                        <input {...getInputProps()} />
                        {isBrowser && <Typography variant={'body1'}>{t('info.dropFileHere')}</Typography>}
                        {(!over || !dropped) &&
                            <Fragment>
                                <Button onClick={open} sx={classes.button} disableElevation
                                    disabled={!!props.disabled ? props.disabled : false}>
                                    {t('info.browseFiles')}
                                </Button>
                                <Typography variant="caption">
                                    {acceptedFilesLabel}
                                </Typography>
                            </Fragment>
                        }
                    </Fragment>
                    :
                    <Box sx={{ width: '90%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <Box sx={{ width: '100%', marginTop: 1 }}>
                                <LinearProgress
                                    sx={[classes.root, {
                                        "& .MuiLinearProgress-bar": (error ? classes.barError : classes.bar)
                                    }]}
                                    variant="determinate" value={progress} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', marginTop: 2 }}>
                                <Typography variant="h6">
                                    {error ? errorMessage : `${Math.round(progress,)}% ${t('success.completed')}`}
                                </Typography>
                                <Typography variant="caption">
                                    {uploadedFiles.length > 0 && !error && uploadedFiles[0].name}
                                </Typography>
                            </Box>
                            {(!!props.onRemove || error) &&
                                <Button onClick={cancel} sx={classes.button} disableElevation
                                        disabled={!!props.disabled ? props.disabled : false}>
                                    {t('info.removeFile')}
                                </Button>}
                        </Box>
                    </Box>
                }
            </Box>
        </Fragment>
    );
};

export default FileUploader;